import {
  ACESFilmicToneMapping,
  EquirectangularReflectionMapping,
  PerspectiveCamera,
  Quaternion,
  Scene,
  WebGLRenderer,
} from "three";
import { IPipelineModule } from ".";
import {
  EJPlayerBase,
  EJPlayerCubeOne,
  EJWebGLRenderer,
  EJPlayer,
  EJPlayerPortals,
} from "../libs/ejx.es.js";
import mitt, { Emitter } from "mitt";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import HDRImage from "/empty_warehouse_01_1k.hdr";
import { RendererEvents } from "../index.js";

export type EJPlayerPipelineModuleEvents = {
  setup: {
    player: EJPlayerBase;
  };
  resized: {
    width: number;
    height: number;
    dpi: number;
  };
  trackingStatus: string;
  "place-object": TouchEvent;
  "tracking-status": "LIMITED" | "NORMAL";
};

export type EJPlayerPipelineModuleResult = IPipelineModule & {
  emitter: Emitter<RendererEvents>;
};

export const ejPlayerPipelineModule = (): EJPlayerPipelineModuleResult => {
  let player: EJPlayerBase | undefined;
  const emitter = mitt<EJPlayerPipelineModuleEvents>();
  let allowCanvasResize = true;
  let updatedCanvasWidth = 0;
  let updatedCanvasHeight = 0;

  return {
    name: "ej-player",
    emitter,
    onStart: ({ canvas, GLctx }) => {
      const scene = new Scene();
      const camera = new PerspectiveCamera(
        75,
        window.innerWidth / window.innerHeight,
        0.1,
        1000
      );
      camera.position.set(0, 1.5, 0);

      const renderer = new EJWebGLRenderer({
        canvas,
        context: GLctx,
      });
      renderer.autoClear = false;

      renderer.toneMapping = ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1;

      new RGBELoader().load(HDRImage, function (texture) {
        texture.mapping = EquirectangularReflectionMapping;

        scene.environment = texture;
      });

      // Sync the xr controller's 6DoF position and camera paremeters with our scene.
      XR8.XrController.updateCameraProjectionMatrix({
        origin: camera.position,
        facing: camera.quaternion,
      });

      player = new EJPlayer({
        canvas,
        // camera,
        renderer,
        scene,
      });
      if (player) {
        if (player.controls) {
          player.controls.dispose(); // remove any existing controls.
          player.controls = null;
        }
        if (player.orbitControls) {
          player.orbitControls.dispose(); // remove any existing orbit controls.
          player.orbitControls = null;
        }
        player.renderer.setSize(window.innerWidth, window.innerHeight);

        player.resize();

        emitter.emit("setup", { player });
      }
    },
    onUpdate: ({ processCpuResult }) => {
      if (!player) {
        console.warn(
          "ejPlayerPipelineModule: onUpdate but no `player` to update."
        );
        return;
      }
      if (
        processCpuResult &&
        processCpuResult.reality &&
        processCpuResult.reality.trackingStatus
      ) {
        emitter.emit("trackingStatus", processCpuResult.reality.trackingStatus);
      }
      const realitySource =
        processCpuResult.reality || processCpuResult.facecontroller;

      if (!realitySource) {
        return;
      }

      const { rotation, position, intrinsics } = realitySource;
      const { camera } = player;

      for (let i = 0; i < 16; i++) {
        camera.projectionMatrix.elements[i] = intrinsics[i];
      }

      // Fix for broken raycasting in r103 and higher. Related to:
      //   https://github.com/mrdoob/three.js/pull/15996
      // Note: camera.projectionMatrixInverse wasn't introduced until r96 so check before setting
      // the inverse
      if (camera.projectionMatrixInverse) {
        camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert();
      }

      if (rotation) {
        camera.setRotationFromQuaternion(rotation as Quaternion);
      }
      if (position) {
        camera.position.set(position.x, position.y, position.z);
      }
      camera.updateMatrix();
      camera.updateMatrixWorld();
    },
    onCanvasSizeChange: ({ canvasWidth, canvasHeight }) => {
      if (!player) {
        console.warn(
          "ejPlayerPipelineModule: onCanvasSizeChange but no `player` to update."
        );
        return;
      }
      const devicePixelRatio = 1;
      const updateCameraAndRenderer = true;
      // const canvasWidth = window.innerWidth;
      // const canvasHeight = window.innerHeight;

      // temp hacky fix for the canvas resize breaking after an orientation change
      if (allowCanvasResize) {
        updatedCanvasWidth = canvasWidth;
        updatedCanvasHeight = canvasHeight;
      }
      player.renderer.setSize(canvasWidth, canvasHeight);
      player.resize(
        updatedCanvasWidth,
        updatedCanvasHeight,
        devicePixelRatio,
        updateCameraAndRenderer
      );
      emitter.emit("resized", {
        width: canvasWidth,
        height: canvasHeight,
        dpi: devicePixelRatio,
      });
      allowCanvasResize = true;
    },
    onDeviceOrientationChange: () => {
      allowCanvasResize = false;
    },

    onRender: () => {
      if (!player) {
        console.warn(
          "ejPlayerPipelineModule: onRender but no `player` to update."
        );
        return;
      }

      player.render();
      player.renderer.clearDepth();
    },
  };
};
